import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { LayerData, LayerItem } from '../../services/types';
import { formatDate } from '../../utils/helpers';
import { LABELS } from '../../utils/constants';

// Function to clean attribute name strings like jobs_created
function formatAttributeName(str: string) {
    if (!str) return;

    return str
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
}

function MapItemDetail() {
    const [searchParams] = useSearchParams();
    const layerId = searchParams.get('layerId');
    const pointId = searchParams.get('id');

    const navigate = useNavigate();

    const [layerItem, setLayerItem] = useState<LayerItem | null>(null);
    const [initiativeData, setInitiativeData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    // const [year, setYear] = useState<string>('');
    // const currFisYear = calculateYearByType(year);

    // Handler for edit click
    const handleEditClick = () => {
        if (!initiativeData || !layerItem?.external_id) return;

        navigate(`/commerce-${LABELS.ENTITY_NAME}/edit/${layerItem.external_id}`, {
            state: {
                initiativeData,
                from: window.location.pathname,
            },
        });
    };

    console.log('initiative id:', layerItem?.external_id);

    // Fetch initiative data once layerItem is available
    useEffect(() => {
        const fetchInitiative = async () => {
            if (!layerItem?.external_id) return;

            try {
                const response = await fetch(`${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getinitiativebyid`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: layerItem.external_id
                    }),
                });

                const data = await response.json();
                if (data) {
                    setInitiativeData(data);
                } else {
                    throw new Error('No initiative returned');
                }
            } catch (err) {
                console.error('Failed to fetch initiative:', err);
                setError('Could not load initiative details.');
            }
        };

        fetchInitiative();
    }, [layerItem]);

    useEffect(() => {
        const fetchLayerDataAndCompany = async () => {
            if (!layerId || !pointId) return;
            // setLoading(true);

            try {
                //Fetch layer items
                const response = await fetch(
                    `${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getlayeritems?layerId=${layerId}`,
                    {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json' },
                    }
                );

                if (!response.ok) throw new Error('Failed to fetch layer items');

                const layerData: LayerData = await response.json();
                const layerItems = layerData.items;

                //Find the item by internal `id`
                const matchedItem = layerItems.find((item) => item.id.toString() === pointId);
                if (!matchedItem) {
                    throw new Error(`No matching item found in layer for pointId: ${pointId}`);
                }

                setLayerItem(matchedItem);
            } catch (err: any) {
                console.error('Error during layer/company load:', err);
                Sentry.captureException(err);
                setError('Failed to load company information.');
            } finally {
                // setLoading(false);
            }
        };

        fetchLayerDataAndCompany();
    }, [layerId, pointId]);

    return (
        <div className="p-6">
            {error && (
                <div className="text-red-500 text-sm mb-4">{error}</div>
            )}

            {layerItem && (
                <div className="mb-6 bg-white rounded-md shadow-lg border border-gray-100 overflow-hidden">
                    <div className="p-6 bg-[#4d4d4d] rounded-t-xl flex justify-between items-center">
                        <p className="text-xl font-bold text-white capitalize">
                            {layerItem.name || `Unnamed ${LABELS.ENTITY_NAME}`}
                        </p>
                        <button
                            className="px-4 py-2 text-sm text-white font-bold w-36 bg-button-blue rounded-md"
                            onClick={handleEditClick}
                        >
                            Edit {LABELS.ENTITY_NAME}
                        </button>
                    </div>

                    <div className="p-6 space-y-2 text-gray-700 text-sm">
                        <div className="flex items-center space-x-2">
                            <span className="font-semibold">Description:</span>
                            <span>{layerItem.description || 'N/A'}</span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <span className="font-semibold">Category:</span>
                            <span>{layerItem.category || 'N/A'}</span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <span className="font-semibold">Activity Date:</span>
                            <span>{formatDate(layerItem.activity_date)}</span>
                        </div>

                        <div className='border border-gray-300 rounded-t-xl border-t-0'>
                            <div className="p-4 bg-color-cai-dashboard rounded-t-xl flex justify-between items-center">
                                <h2 className='text-white font-bold'>Wins</h2>
                            </div>
                            {layerItem.attributes.map((attr, index) => (
                                <div
                                    key={index}
                                    className={`grid grid-cols-2 items-center border-b border-gray-200 last:border-b-0 ${
                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                    }`}
                                >
                                    <div className="py-2 px-3 font-medium text-xs border-r border-gray-200">
                                    {formatAttributeName(attr.name)}
                                    </div>
                                    <div className="p-3 text-xs">
                                    <p>{attr.value}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MapItemDetail;
